// src/contexts/geral/PrinterComandaContext.js
import React, { createContext, useContext, useCallback } from 'react';
import { formatCurrency } from '../../utils/numberUtils';
import { formatDate, formatTime } from '../../utils/dateTimeUtils';
import { useMessage } from '../../contexts/geral/MessageContext';
import { fetchData } from '../../services/Api';
import { getComandaPDFByPedidoIdService, getComandasPDFByIdsService } from '../../services/pedidos/PedidoService';

// Criar o contexto para impressão de comandas
const PrinterComandaContext = createContext();

export const PrinterComandaProvider = ({ children }) => {
  const { showMessage } = useMessage();

  // 🧾 Gera o HTML da impressão das comandas selecionadas
  const gerarResumoComandas = async (mesa, comandasSelecionadas) => {
    try {
      let recibo = `
      <html>
      <head>
        <title>Resumo de Comandas</title>
        <style>
          @page { size: 74mm auto; margin: 0; }
          html, body {
            width: 74mm;
            height: auto !important;
            overflow: visible !important;
            margin: 0;
            padding: 0;
          }

          body {
            font-family: 'Draft', monospace;
            font-size: 12px;
            padding: 6px;
            max-width: 74mm; /* ✅ em vez de width fixa */
            box-sizing: border-box;
          }

          h2, h3 { text-align: center; margin: 2px 0; }
          hr { border: 1px dashed black; }
          p { margin: 2px 0; }
          .right { text-align: right; }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .total { font-size: 14px; font-weight: bold; }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 4px; 
            table-layout: fixed; 
          }

          th.item, td.item { width: 60%; text-align: left; }
          th.qtde, td.qtde { width: 12%; text-align: center; }
          th.total, td.total { width: 28%; text-align: right; }

          th, td { font-size: 12px; padding: 2px 0; word-wrap: break-word; }
          th { text-align: left; border-bottom: 1px dashed black; }
          th.valor, td.valor {
            text-align: right;
          }
        </style>

      </head>
      <body>
      <hr>
      <h2>RESUMO DE COMANDAS</h2>
      <hr>
      <h3 style="text-align: left; margin: 0;">Mesa: ${mesa?.codigo || 'N/A'}</h3>
      <p><strong>Data:</strong> ${formatDate(new Date())} ${formatTime(new Date())}</p>
      <hr>
      `;

      // ✅ Loop pelas comandas selecionadas
      comandasSelecionadas.forEach((comanda) => {
        recibo += `
          <h3>Comanda #${comanda.codigo}</h3>

          <table>
            <thead>
              <tr>
                <th class="item">Item</th>
                <th class="qtde">Qtde</th>
                <th class="total">Total</th>
              </tr>
            </thead>
            <tbody>
        `;

        comanda.comandaItens?.forEach((item) => {
          recibo += `
            <tr>
              <td class="item">${item.xProd || item.produto?.descricao || '—'}</td>
              <td class="qtde">${item.qtde}</td>
              <td class="total">${formatCurrency(item.valorTotal || 0)}</td>
            </tr>
          `;

        });

        const totalComanda = comanda.comandaItens?.reduce((sum, i) => sum + (i.valorTotal || 0), 0) || 0;

        recibo += `
            </tbody>
          </table>
          <p class="right total">Total Comanda: ${formatCurrency(totalComanda)}</p>
          <hr>
        `;
      });

      const totalMesa = comandasSelecionadas.reduce((acc, c) => {
        const total = c.comandaItens?.reduce((sum, i) => sum + (i.valorTotal || 0), 0) || 0;
        return acc + total;
      }, 0);

      // ✅ Só mostra TOTAL GERAL se tiver mais de 1 comanda
      if (comandasSelecionadas.length > 1) {
        recibo += `
          <h2 class="total">TOTAL GERAL: ${formatCurrency(totalMesa)}</h2>
          <hr>
        `;
      }

      recibo += `
        <p class="center" style="margin-top: 20px;">Obrigado pela preferência!</p>
      </body>
      </html>
      `;


      return recibo;
    } catch (error) {
      showMessage('Erro ao gerar resumo de comandas.', 'danger');
      console.error(error);
      return '';
    }
  };

  // 🖨️ Função para imprimir o resumo
  const printResumoComandas = useCallback(async (mesa, comandasSelecionadas) => {
    try {
      const conteudoImpressao = await gerarResumoComandas(mesa, comandasSelecionadas);
      if (!conteudoImpressao) return;

      const janelaImpressao = window.open('', '', 'width=800,height=600');
      janelaImpressao.document.write(conteudoImpressao);
      janelaImpressao.document.close();
      janelaImpressao.onload = () => {
        janelaImpressao.print();
        janelaImpressao.close();
      };
    } catch (error) {
      showMessage('Erro ao imprimir resumo das comandas.', 'danger');
      console.error(error);
    }
  }, [showMessage]);

  const printResumoComandasPDF = useCallback(async (mesa, comandasSelecionadas, paperWidthMm = 75) => {
    try {
      const comandaIds = (comandasSelecionadas || [])
        .map((comanda) => comanda?.id)
        .filter((comandaId) => comandaId || comandaId === 0);

      if (!comandaIds.length) {
        showMessage('Nenhuma comanda informada para impressão.', 'warning');
        return false;
      }

      const pdfBlob = await getComandasPDFByIdsService({ comandaIds, paperWidthMm });
      const blob = pdfBlob?.type === 'application/pdf'
        ? pdfBlob
        : new Blob([pdfBlob], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);
      const frameExistente = document.getElementById('comandas-pdf-print-frame');

      if (frameExistente) {
        frameExistente.remove();
      }

      const frame = document.createElement('iframe');
      frame.id = 'comandas-pdf-print-frame';
      frame.title = 'Impressão de Resumo de Comandas';
      frame.style.position = 'fixed';
      frame.style.right = '0';
      frame.style.bottom = '0';
      frame.style.width = '1px';
      frame.style.height = '1px';
      frame.style.border = '0';
      frame.style.opacity = '0';
      frame.style.pointerEvents = 'none';
      frame.src = pdfUrl;
      document.body.appendChild(frame);

      let impressaoSolicitada = false;
      let cleanupSolicitado = false;

      const cleanup = () => {
        if (cleanupSolicitado) {
          return;
        }
        cleanupSolicitado = true;
        frame.remove();
        URL.revokeObjectURL(pdfUrl);
      };

      const imprimirPDF = () => {
        if (impressaoSolicitada) {
          return;
        }
        impressaoSolicitada = true;

        try {
          frame?.contentWindow?.focus();
          frame?.contentWindow?.print();
        } catch (error) {
          console.error('Erro ao abrir a impressão do PDF das comandas:', error);
          showMessage('Não foi possível abrir a impressão do PDF das comandas.', 'warning');
          cleanup();
        } finally {
          frame.contentWindow?.addEventListener?.('afterprint', cleanup, { once: true });
          setTimeout(cleanup, 60000);
        }
      };

      frame.onload = () => setTimeout(imprimirPDF, 300);
      setTimeout(imprimirPDF, 1200);

      return true;
    } catch (error) {
      showMessage('Erro ao imprimir o PDF das comandas.', 'danger');
      console.error(error);
      return false;
    }
  }, [showMessage]);

  const ComandaPDFPorPedidoId = useCallback(async (pedidoId, paperWidthMm = 75) => {
    try {
      if (!pedidoId && pedidoId !== 0) {
        showMessage('Pedido não informado para impressão.', 'warning');
        return false;
      }

      const pdfBlob = await getComandaPDFByPedidoIdService({ pedidoId, paperWidthMm });
      const blob = pdfBlob?.type === 'application/pdf'
        ? pdfBlob
        : new Blob([pdfBlob], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);
      const frameExistente = document.getElementById('comanda-pedido-pdf-print-frame');

      if (frameExistente) {
        frameExistente.remove();
      }

      const frame = document.createElement('iframe');
      frame.id = 'comanda-pedido-pdf-print-frame';
      frame.title = 'Impressão de Comanda por Pedido';
      frame.style.position = 'fixed';
      frame.style.right = '0';
      frame.style.bottom = '0';
      frame.style.width = '1px';
      frame.style.height = '1px';
      frame.style.border = '0';
      frame.style.opacity = '0';
      frame.style.pointerEvents = 'none';
      frame.src = pdfUrl;
      document.body.appendChild(frame);

      let impressaoSolicitada = false;
      let cleanupSolicitado = false;

      const cleanup = () => {
        if (cleanupSolicitado) {
          return;
        }
        cleanupSolicitado = true;
        frame.remove();
        URL.revokeObjectURL(pdfUrl);
      };

      const imprimirPDF = () => {
        if (impressaoSolicitada) {
          return;
        }
        impressaoSolicitada = true;

        try {
          frame?.contentWindow?.focus();
          frame?.contentWindow?.print();
        } catch (error) {
          console.error('Erro ao abrir a impressão do PDF da comanda:', error);
          showMessage('Não foi possível abrir a impressão do PDF da comanda.', 'warning');
          cleanup();
        } finally {
          frame.contentWindow?.addEventListener?.('afterprint', cleanup, { once: true });
          setTimeout(cleanup, 60000);
        }
      };

      frame.onload = () => setTimeout(imprimirPDF, 300);
      setTimeout(imprimirPDF, 1200);

      return true;
    } catch (error) {
      showMessage('Erro ao imprimir o PDF da comanda.', 'danger');
      console.error(error);
      return false;
    }
  }, [showMessage]);

  const gerarHtmlItensComandaSemPedido = async (comandaId, item = 0) => {
    try {
      const itens = await fetchData(
        `/ComandaItem/GetItensSemPedido?comandaId=${comandaId}&item=${item}`
      );
      const comanda = await fetchData(
        `/Comanda/GetById?comandaId=${comandaId}`,
      );

      const mesa = await fetchData(
        `/Mesa/GetMesaById?mesaId=${comanda.mesaId}`,
      );

      // if (!itens || itens.length === 0) {
      //   showMessage('Nenhum item sem pedido para esta comanda.', 'info');
      //   return '';
      // }

      let recibo = `
      <html>
      <head>
        <title>Itens sem Pedido</title>
        <style>
          @page { size: 80mm auto; margin: 0; }

          body {
            width: 79mm;
            max-width: 79mm;
            margin: 0 auto;
            padding: 3.5mm 3.5mm;
            box-sizing: border-box;
            font-family: monospace;
            font-size: 14px;
          }

          h3 {
            text-align: center;
            margin: 1px 0;
            font-size: 16px;
          }

          hr {
            border: 1px dashed black;
            margin: 3px 0;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          th, td {
            padding: 1px 0;
            font-size: 14px;
          }

          th {
            border-bottom: 1px dashed black;
            text-align: left;
          }

          th.item, td.item { width: 60%; }
          th.qtde, td.qtde { width: 8%; text-align: right; }
          th.total, td.total { width: 32%; text-align: right; }

          td.item {
            white-space: normal;
            word-break: break-word;
          }

          .total-final {
            font-size: 16px;
            font-weight: bold;
            text-align: right;
          }
        </style>



      </head>
      <body>
        <hr>
        <h3>Mesa #${mesa.codigo}</h3>
        <h3>Comanda #${comanda.codigo}</h3>
        <hr>        
        <p><strong>Data:</strong> ${formatDate(new Date())} ${formatTime(new Date())}</p>
        <hr>

        <table>
          <thead>
            <tr>
              <th class="item">Item</th>
              <th class="qtde">Qtd</th>
              <th class="total">Total</th>
            </tr>
          </thead>

          <tbody>
      `;

      itens.forEach((item) => {
        recibo += `
          <tr>
            <td class="item">${item.xProd || item.produto?.descricao || '—'}</td>
            <td class="qtde">${item.qtde}</td>
            <td class="total">${formatCurrency(item.valorTotal || 0)}</td>
          </tr>
        `;
      });

      const total = itens.reduce(
        (sum, i) => sum + (i.valorTotal || 0),
        0
      );

      recibo += `
          </tbody>
        </table>

        <hr>
        <p class="total-final">Total: ${formatCurrency(total)}</p>
        <hr>

        <p style="text-align:center;margin-top:15px;">
          Impressão de controle
        </p>
      </body>
      </html>
      `;

      return recibo;
    } catch (error) {
      showMessage('Erro ao gerar impressão dos itens sem pedido.', 'danger');
      console.error(error);
      return '';
    }
  };

  const imprimirItensComandaSemPedido = useCallback(async (comandaId, item) => {
    try {
      if (!comandaId) {
        console.warn("⚠️ comandaId inválido:", comandaId);
        return;
      }

      const html = await gerarHtmlItensComandaSemPedido(comandaId, item);
      if (!html) return;

      const win = window.open('', '', 'width=800,height=600');
      if (!win) {
        showMessage('Popup bloqueado pelo navegador.', 'warning');
        return;
      }

      win.document.write(html);
      win.document.close();
      win.onload = () => {
        win.print();
        win.close();
      };
    } catch (error) {
      showMessage('Erro ao imprimir itens sem pedido.', 'danger');
      console.error(error);
    }
  }, [showMessage]);

  const imprimirComandaPorPedido = useCallback(async (pedido) => {
    try {
      if (!pedido?.comandaId || !pedido?.pedidoItens?.length) return;

      const comanda = await fetchData(`/Comanda/GetById?comandaId=${pedido.comandaId}`);
      const mesa = comanda?.mesaId
        ? await fetchData(`/Mesa/GetMesaById?mesaId=${comanda.mesaId}`)
        : null;

      const itens = pedido.pedidoItens || [];
      const total = itens.reduce((sum, item) => sum + Number(item.valorTotal || 0), 0);

      const html = `
      <html>
      <head>
        <title>Comanda</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          body {
            width: 79mm;
            max-width: 79mm;
            margin: 0 auto;
            padding: 3.5mm 3.5mm;
            box-sizing: border-box;
            font-family: monospace;
            font-size: 14px;
          }
          h3 { text-align: center; margin: 1px 0; font-size: 16px; }
          hr { border: 1px dashed black; margin: 3px 0; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          th, td { padding: 1px 0; font-size: 14px; }
          th { border-bottom: 1px dashed black; text-align: left; }
          th.item, td.item { width: 60%; }
          th.qtde, td.qtde { width: 8%; text-align: right; }
          th.total, td.total { width: 32%; text-align: right; }
          td.item { white-space: normal; word-break: break-word; }
          td.obs {
            padding-left: 8px;
            white-space: normal;
            word-break: break-word;
            font-size: 13px;
          }
          .obs-pedido {
            margin: 2px 0;
            white-space: normal;
            word-break: break-word;
          }
          .total-final { font-size: 16px; font-weight: bold; text-align: right; }
        </style>
      </head>
      <body>
        <hr>
        <h3>Mesa #${mesa?.codigo || "-"}</h3>
        <h3>Comanda #${comanda?.codigo || "-"}</h3>
        <hr>
        <p><strong>Data:</strong> ${formatDate(new Date())} ${formatTime(new Date())}</p>
        ${pedido.observacao?.trim() ? `
          <p class="obs-pedido"><strong>Obs. pedido:</strong> ${pedido.observacao}</p>
        ` : ""}
        <hr>
        <table>
          <thead>
            <tr>
              <th class="item">Item</th>
              <th class="qtde">Qtd</th>
              <th class="total">Total</th>
            </tr>
          </thead>
          <tbody>
            ${itens.map((item) => `
              <tr>
                <td class="item">${item.produto?.descricao || "—"}</td>
                <td class="qtde">${item.qtde || 0}</td>
                <td class="total">${formatCurrency(item.valorTotal || 0)}</td>
              </tr>
              ${item.observacao?.trim() ? `
                <tr>
                  <td colspan="3" class="obs"><strong>Obs. item:</strong> ${item.observacao}</td>
                </tr>
              ` : ""}
            `).join("")}
          </tbody>
        </table>
        <hr>
        <p class="total-final">Total: ${formatCurrency(total)}</p>
        <hr>
        <p style="text-align:center;margin-top:15px;">Impressão de controle</p>
      </body>
      </html>
      `;

      const win = window.open('', '', 'width=800,height=600');
      if (!win) {
        showMessage('Popup bloqueado pelo navegador.', 'warning');
        return;
      }

      win.document.write(html);
      win.document.close();
      win.onload = () => {
        win.print();
        win.close();
      };
    } catch (error) {
      showMessage('Erro ao imprimir comanda do pedido.', 'danger');
      console.error(error);
    }
  }, [showMessage]);



  return (
    <PrinterComandaContext.Provider value={{ printResumoComandas, printResumoComandasPDF, imprimirItensComandaSemPedido, imprimirComandaPorPedido, ComandaPDFPorPedidoId }}>
      {children}
    </PrinterComandaContext.Provider>
  );
};

// Hook para usar o contexto
export const usePrinterComanda = () => {
  const context = useContext(PrinterComandaContext);
  if (!context) {
    throw new Error("usePrinterComanda must be used within a PrinterComandaProvider");
  }
  return context;
};
